/**
 * DecisionFlow — PlanLibraryPopup
 * ui/src/components/admin/PlanLibraryPopup.jsx
 * S611
 *
 * Know More popup for Plan Library certified packages.
 * Spec authority: comp_policies_spec_v2_2.md §4
 *
 * - Does NOT close on outside click (matches AmendmentBeginModal + DeploymentWizard pattern)
 * - Close: X button + Escape key only
 * - Footer is context-aware by tenant_deployment_status
 * - Default Component Attributes panel shows data-fit ICM defaults before deploy
 * - Certification badge shows platform_version
 * - Accessibility: role=dialog, aria-modal, focus trap, aria-labelledby
 * - Mobile: full-screen sheet on viewports < 768px
 *
 * CSS: rgb(var(--color-*)) tokens only. Zero hardcoded Tailwind colors. Zero dark: variants.
 */

import { useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { X, Sparkles, ExternalLink } from "lucide-react";

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------
function SectionBlock({ title, content }) {
  if (!content) {
    return (
      <div>
        <h3 className="mb-1.5 text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]">
          {title}
        </h3>
        <p className="text-tok-sm text-[rgb(var(--color-text-muted))] italic">
          Content coming soon for this package.
        </p>
      </div>
    );
  }
  return (
    <div>
      <h3 className="mb-1.5 text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]">
        {title}
      </h3>
      <p className="text-tok-sm leading-relaxed text-[rgb(var(--color-text))]">{content}</p>
    </div>
  );
}

function ModuleRow({ module }) {
  return (
    <div className="flex items-start gap-3 py-1.5 border-b border-[rgb(var(--color-border))] last:border-0">
      <span className="min-w-0 flex-1 text-tok-xs font-medium text-[rgb(var(--color-text))]">
        {module.module_name ?? module.module_code}
      </span>
      {module.package_role && (
        <span className="text-right text-tok-xs text-[rgb(var(--color-text-muted))]">
          {module.package_role}
        </span>
      )}
    </div>
  );
}

function DefaultAttributesPanel({ defaults = {}, modules = [] }) {
  // Only show non-zero weights
  const weights = [
    { key: "arr_weight",      label: "Revenue (ARR)" },
    { key: "mbo_weight",      label: "MBO" },
    { key: "nps_weight",      label: "NPS" },
    { key: "adoption_weight", label: "Adoption" },
  ].filter((w) => (defaults[w.key] ?? 0) > 0);

  const hasSplit  = defaults.base_pct != null;
  const hasKicker = defaults.kicker_threshold != null;
  const hasCap    = defaults.accelerator_cap != null;
  const hasClawback = defaults.clawback_window_days != null || defaults.clawback_window != null;

  if (!hasSplit && !weights.length && !hasKicker) return null;

  return (
    <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-3 space-y-2">
      <p className="text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]">
        Default Component Attributes
      </p>

      <div className="space-y-1.5">
        {hasSplit && (
          <div className="flex items-center justify-between">
            <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">Base / Variable split</span>
            <span className="text-tok-xs font-medium text-[rgb(var(--color-text))]">
              {Math.round((defaults.base_pct ?? 0.5) * 100)}% / {Math.round((defaults.variable_pct ?? 0.5) * 100)}%
            </span>
          </div>
        )}

        {weights.map((w) => (
          <div key={w.key} className="flex items-center justify-between">
            <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">{w.label} weight</span>
            <span className="text-tok-xs font-medium text-[rgb(var(--color-text))]">
              {Math.round((defaults[w.key] ?? 0) * 100)}%
            </span>
          </div>
        ))}

        {hasKicker && (
          <div className="flex items-center justify-between">
            <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">Kicker threshold</span>
            <span className="text-tok-xs font-medium text-[rgb(var(--color-text))]">
              {Math.round((defaults.kicker_threshold ?? 1) * 100)}% attainment
            </span>
          </div>
        )}

        {hasKicker && defaults.kicker_multiplier != null && (
          <div className="flex items-center justify-between">
            <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">Kicker multiplier</span>
            <span className="text-tok-xs font-medium text-[rgb(var(--color-text))]">
              {defaults.kicker_multiplier}×
            </span>
          </div>
        )}

        {hasCap && (
          <div className="flex items-center justify-between">
            <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">Accelerator cap</span>
            <span className="text-tok-xs font-medium text-[rgb(var(--color-text))]">
              {Math.round((defaults.accelerator_cap ?? 2.5) * 100)}% of variable target
            </span>
          </div>
        )}

        {hasClawback && (
          <div className="flex items-center justify-between">
            <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">Clawback window</span>
            <span className="text-tok-xs font-medium text-[rgb(var(--color-text))]">
              {defaults.clawback_window_days ?? defaults.clawback_window} days post-close
            </span>
          </div>
        )}

        <div className="flex items-center justify-between pt-1 border-t border-[rgb(var(--color-border))]">
          <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">OTE range</span>
          <span className="text-tok-xs italic text-[rgb(var(--color-text-muted))]">
            Enter your organisation's values on deploy
          </span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Footer — context-aware by deployment status
// ---------------------------------------------------------------------------
function PopupFooter({ template, onDeploy, onClose }) {
  const status     = template.tenant_deployment_status ?? "NOT_DEPLOYED";
  const activePlans = template.active_plan_ids ?? [];
  const isComingSoon = template.is_coming_soon;

  if (isComingSoon) {
    return (
      <div className="flex items-center justify-between border-t border-[rgb(var(--color-border))] px-6 py-4">
        <span className="text-tok-xs italic text-[rgb(var(--color-text-muted))]">
          Coming Soon — Available in a future release
        </span>
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg border border-[rgb(var(--color-border))] px-4 py-2
                     text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]"
        >
          Close
        </button>
      </div>
    );
  }

  if (status === "PENDING_APPROVAL") {
    return (
      <div className="border-t border-[rgb(var(--color-border))] px-6 py-4 space-y-3">
        <p className="text-tok-xs text-[rgb(var(--color-warning))] font-medium">
          Pending Approval — awaiting second approver
        </p>
        <div className="flex items-center justify-between">
          <button type="button" onClick={onClose}
            className="rounded-lg border border-[rgb(var(--color-border))] px-4 py-2
                       text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]">
            Close
          </button>
          <button type="button" onClick={onClose}
            className="rounded-lg bg-[rgb(var(--color-primary))]/10 px-4 py-2
                       text-tok-xs font-medium text-[rgb(var(--color-primary))] hover:bg-[rgb(var(--color-primary))]/20">
            View in Approvals →
          </button>
        </div>
      </div>
    );
  }

  if (status === "ACTIVE") {
    return (
      <div className="border-t border-[rgb(var(--color-border))] px-6 py-4 space-y-3">
        {activePlans.length > 0 && (
          <div className="space-y-1">
            <p className="text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] uppercase tracking-wide">
              Deployed Plans
            </p>
            {activePlans.slice(0, 3).map((planId) => (
              <div key={planId} className="flex items-center justify-between py-1">
                <span className="text-tok-xs text-[rgb(var(--color-text))]">Plan {planId}</span>
                <span className="text-tok-xs text-[rgb(var(--color-primary))] hover:underline cursor-pointer">View →</span>
              </div>
            ))}
            <div className="border-t border-[rgb(var(--color-border))] pt-2" />
          </div>
        )}
        <div className="flex items-center justify-between">
          <button type="button" onClick={onClose}
            className="rounded-lg border border-[rgb(var(--color-border))] px-4 py-2
                       text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]">
            Cancel
          </button>
          <button type="button" onClick={() => onDeploy(template)}
            className="rounded-lg bg-[rgb(var(--color-primary))] px-4 py-2
                       text-tok-xs font-semibold text-white hover:opacity-90">
            Deploy Another Instance →
          </button>
        </div>
      </div>
    );
  }

  if (status === "COMPONENTS_PENDING") {
    return (
      <div className="flex items-center justify-between border-t border-[rgb(var(--color-border))] px-6 py-4">
        <button type="button" onClick={onClose}
          className="rounded-lg border border-[rgb(var(--color-border))] px-4 py-2
                     text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]">
          Cancel
        </button>
        <button type="button" onClick={onClose}
          className="rounded-lg bg-[rgb(var(--color-primary))] px-4 py-2
                     text-tok-xs font-semibold text-white hover:opacity-90">
          Configure Components →
        </button>
      </div>
    );
  }

  // NOT_DEPLOYED — primary state
  return (
    <div className="flex items-center justify-between border-t border-[rgb(var(--color-border))] px-6 py-4">
      <button type="button" onClick={onClose}
        className="rounded-lg border border-[rgb(var(--color-border))] px-4 py-2
                   text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]">
        Cancel
      </button>
      <button type="button" onClick={() => onDeploy(template)}
        className="rounded-lg bg-[rgb(var(--color-primary))] px-5 py-2
                   text-tok-xs font-semibold text-white hover:opacity-90 transition-opacity">
        Deploy this Package →
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main popup
// ---------------------------------------------------------------------------
export default function PlanLibraryPopup({ template, seen, period, onClose, onDeploy }) {
  const dialogRef   = useRef(null);
  const closeBtnRef = useRef(null);

  const gtm      = template.gtm ?? {};
  const defaults = template.component_defaults ?? {};
  const modules  = template.modules ?? [];

  // Focus trap + Escape key
  useEffect(() => {
    closeBtnRef.current?.focus();

    function handleKeyDown(e) {
      if (e.key === "Escape") { onClose(); return; }
      if (e.key !== "Tab") return;
      const focusable = dialogRef.current?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      ) ?? [];
      if (!focusable.length) return;
      const first = focusable[0];
      const last  = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const isMobile = window.innerWidth < 768;

  return (
    <>
      {/* Overlay — does NOT close on click (spec §4) */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-40 bg-black/40"
      />

      {/* Centering wrapper — flex so popup centers within visible viewport
          regardless of Copilot panel or any right-side overlay.
          px-4 on desktop gives breathing room on narrow viewports. */}
      <div className={`fixed inset-0 z-50 flex items-center justify-center${isMobile ? "" : " px-4"}`}>

        {/* Dialog */}
        <motion.div
          initial={{ opacity: 0, scale: isMobile ? 1 : 0.97, y: isMobile ? "100%" : 0 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: isMobile ? 1 : 0.97, y: isMobile ? "100%" : 0 }}
          transition={{ duration: 0.18 }}
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="plan-library-popup-title"
          className={`flex flex-col overflow-hidden bg-[rgb(var(--color-surface))]
                      shadow-2xl border border-[rgb(var(--color-border))]
                      ${isMobile
                        ? "fixed inset-0 rounded-none"
                        : "relative w-full max-w-[720px] max-h-[85vh] rounded-2xl"}`}
        >
        {/* ── Sticky header ── */}
        <div className="shrink-0 border-b border-[rgb(var(--color-border))] px-6 pt-5 pb-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h2
                id="plan-library-popup-title"
                className="text-tok-lg font-bold text-[rgb(var(--color-text))] leading-tight"
              >
                {template.template_name}
              </h2>

              {template.similar_to && (
                <p className="mt-0.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
                  Similar to: {template.similar_to}
                </p>
              )}

              {/* Pill row */}
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                {template.sales_motion && (
                  <span className="rounded-full border border-[rgb(var(--color-border))] px-2 py-0.5 text-[10px] font-medium text-[rgb(var(--color-text-muted))]">
                    {template.sales_motion.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                  </span>
                )}
                {template.tier && (
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium
                    ${template.tier === "enterprise"
                      ? "bg-[rgb(var(--color-primary))]/10 text-[rgb(var(--color-primary))]"
                      : "bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]"}`}>
                    {template.tier === "enterprise" ? "Enterprise" : "Standard"}
                  </span>
                )}
                {(template.industry_tags ?? []).map((ind) => (
                  <span key={ind} className="rounded-full border border-[rgb(var(--color-border))] px-2 py-0.5 text-[10px] text-[rgb(var(--color-text-muted))]">
                    {ind}
                  </span>
                ))}
              </div>

              {/* Module chips */}
              {modules.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {modules.map((m) => (
                    <span key={m.module_code}
                      className="rounded px-1.5 py-0.5 text-[10px] font-medium
                                 bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]">
                      {m.module_name ?? m.module_code}
                    </span>
                  ))}
                </div>
              )}

              {/* Certification badge */}
              {template.is_platform_certified && (
                <div className="mt-2 flex items-center gap-1.5">
                  <div className="h-px flex-1 bg-[rgb(var(--color-border))]" />
                  <span className="flex items-center gap-1 text-[10px] text-[rgb(var(--color-text-muted))]">
                    <Sparkles className="h-3 w-3" />
                    data-fit Curated
                    {template.platform_version && ` · ${template.platform_version}`}
                  </span>
                  <div className="h-px flex-1 bg-[rgb(var(--color-border))]" />
                </div>
              )}
            </div>

            {/* Close button */}
            <button
              ref={closeBtnRef}
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="shrink-0 rounded-lg p-1.5 text-[rgb(var(--color-text-muted))]
                         hover:bg-[rgb(var(--color-surface-muted))] transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* ── Scrollable body ── */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

          <SectionBlock title="1 — Objective"          content={gtm.objective} />
          <SectionBlock title="2 — Behavior It Drives" content={gtm.behavioral_driver} />
          <SectionBlock title="3 — Results You Can Expect" content={gtm.expected_results} />

          {/* Applicability */}
          <div>
            <h3 className="mb-1.5 text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]">
              4 — Applicability
            </h3>
            {gtm.applicability ? (
              <p className="text-tok-sm leading-relaxed text-[rgb(var(--color-text))]">
                {gtm.applicability}
              </p>
            ) : (
              <p className="text-tok-sm italic text-[rgb(var(--color-text-muted))]">
                Content coming soon for this package.
              </p>
            )}
            {gtm.not_recommended_for && (
              <p className="mt-1.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
                <strong className="font-medium">Not recommended for:</strong> {gtm.not_recommended_for}
              </p>
            )}
          </div>

          {/* Modules Included */}
          {modules.length > 0 && (
            <div>
              <h3 className="mb-2 text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]">
                Modules Included
              </h3>
              <div className="rounded-lg border border-[rgb(var(--color-border))] divide-y divide-[rgb(var(--color-border))]">
                {modules.map((m) => (
                  <ModuleRow key={m.module_code} module={m} />
                ))}
              </div>
            </div>
          )}

          {/* Default Component Attributes */}
          <DefaultAttributesPanel defaults={defaults} modules={modules} />

        </div>

        {/* ── Fixed footer ── */}
        <div className="shrink-0">
          <PopupFooter
            template={template}
            onDeploy={onDeploy}
            onClose={onClose}
          />
        </div>
        </motion.div>

      </div>{/* end centering wrapper */}
    </>
  );
}
